import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'

const heroSlide1 = '/assets/hero-slide-1.png'
const heroSlide2 = '/assets/hero-slide-2.png'
const heroSlide3 = '/assets/hero-slide-3.png'
const AUTOPLAY_MS = 5000

const textReveal = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.35, ease: [0.4, 0, 1, 1] },
  },
}

const textStagger = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.08 },
  },
  exit: {
    transition: { staggerChildren: 0.06, staggerDirection: -1 },
  },
}

const slides = [
  {
    id: 1,
    image: heroSlide1,
    alt: 'Industrial metal coils in warehouse',
    eyebrow: 'Trusted by 100+ Buyers Across 50 Countries',
    titleBefore: 'Your Global Metal Trading Partner —',
    titleHighlight: 'From India to the World',
    titleAfter: '',
    description:
      'We bridge Indian manufacturing capability with international demand — procurement, documentation, and last-mile delivery managed end-to-end.',
    features: ['Global Sourcing', 'Timely Delivery', 'Trusted Partner'],
  },
  {
    id: 2,
    image: heroSlide2,
    alt: 'Metal fabrication facility with overhead crane',
    eyebrow: "Gujarat's Premier Metal Processor",
    titleBefore: 'Factory-Grade Metal Processing,',
    titleHighlight: 'Delivered on Time',
    titleAfter: '',
    description:
      'BIS-compliant fabrication, cold storage, and same-day dispatch from our Sabarkantha facility — zero compromise on quality.',
    features: ['ISO-Aligned Quality', 'Bulk Capacity', 'On-Time Dispatch'],
  },
  {
    id: 3,
    image: heroSlide3,
    alt: 'Workers handling metal components in factory',
    eyebrow: '6 Metals. 50+ Countries. 500+ Deliveries.',
    titleBefore: 'Six Premium Metals,',
    titleHighlight: 'One Reliable Source',
    titleAfter: '',
    description:
      'Copper, aluminium, zinc, brass, stainless steel, and lead — in every grade and form your operation demands.',
    features: ['50+ Countries', '500+ Deliveries', '10+ Years Experience'],
  },
]

function HeroSlider() {
  const [activeIndex, setActiveIndex] = useState(0)
  const timerRef = useRef(null)
  const activeSlide = slides[activeIndex]

  const goToNext = useCallback(() => {
    setActiveIndex((current) => (current + 1) % slides.length)
  }, [])

  const goToSlide = useCallback((index) => {
    setActiveIndex(index)
  }, [])

  const startAutoplay = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
    }

    timerRef.current = window.setInterval(goToNext, AUTOPLAY_MS)
  }, [goToNext])

  useEffect(() => {
    startAutoplay()
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current)
      }
    }
  }, [startAutoplay])

  const handleDotClick = (index) => {
    goToSlide(index)
    startAutoplay()
  }

  return (
    <section className="corp-home-hero" aria-label="Homepage hero slider" aria-live="polite">
      <div className="corp-hero-slider" aria-hidden="true">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`corp-hero-slide ${index === activeIndex ? 'is-active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
            role="img"
            aria-label={slide.alt}
          />
        ))}
      </div>

      <div className="corp-home-hero-overlay" aria-hidden="true" />

      <div className="container corp-container corp-home-hero-inner">
        <div className="corp-hero-content">
          <div className="corp-hero-text-stage">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSlide.id}
                className="corp-hero-text-panel"
                variants={textStagger}
                initial="hidden"
                animate="show"
                exit="exit"
              >
                <motion.span className="corp-section-subtitle" variants={textReveal}>
                  {activeSlide.eyebrow}
                </motion.span>

                <motion.h1 variants={textReveal}>
                  {activeSlide.titleBefore}{' '}
                  <span>{activeSlide.titleHighlight}</span>
                  {activeSlide.titleAfter ? ` ${activeSlide.titleAfter}` : ''}
                </motion.h1>

                <motion.p variants={textReveal}>{activeSlide.description}</motion.p>

                <motion.div className="hero-actions corp-hero-actions" variants={textReveal}>
                  <Link className="primary-btn corp-btn-primary" to="/products">
                    Explore Products
                  </Link>
                  <Link className="secondary-btn corp-btn-secondary" to="/contact#inquiry-form">
                    Contact Us
                  </Link>
                </motion.div>

        
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="corp-hero-dots" role="tablist" aria-label="Hero slides">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            className={index === activeIndex ? 'is-active' : ''}
            aria-label={`Go to slide ${index + 1}: ${slide.eyebrow}`}
            aria-selected={index === activeIndex}
            onClick={() => handleDotClick(index)}
          >
            {index === activeIndex ? (
              <span
                className="corp-hero-dot-progress"
                style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                aria-hidden="true"
              />
            ) : null}
          </button>
        ))}
      </div>
    </section>
  )
}

export default HeroSlider
